import { useState, useMemo } from "react";
import { sendResendEmail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Send,
  ShieldCheck,
  Wallet,
  Clock,
  ListChecks,
  AlertTriangle,
  Save,
  Sparkles,
  Plus,
  X,
} from "lucide-react";
import { toast } from "sonner";

// Modern Tanımla & Kaldır Butonlu E-posta Listesi Yönetim Bileşeni
function EmailListManager({ label, icon: Icon, iconColor, btnBg, emailsString, onChange, description }) {
  const [inputVal, setInputVal] = useState("");

  const emailList = useMemo(() => {
    if (!emailsString) return [];
    return emailsString.split(",").map((s) => s.trim()).filter(Boolean);
  }, [emailsString]);

  const handleAdd = () => {
    const trimmed = inputVal.trim().toLowerCase();
    if (!trimmed) return;
    if (!trimmed.includes("@") || !trimmed.includes(".")) {
      toast.error("Lütfen geçerli bir e-posta adresi giriniz.");
      return;
    }
    if (emailList.includes(trimmed)) {
      toast.error("Bu e-posta adresi zaten listede tanımlı.");
      return;
    }
    const newList = [...emailList, trimmed];
    onChange(newList.join(", "));
    setInputVal("");
    toast.success(`${trimmed} eklendi.`);
  };

  const handleRemove = (emailToRemove) => {
    const newList = emailList.filter((e) => e !== emailToRemove);
    onChange(newList.join(", "));
    toast.success(`${emailToRemove} kaldırıldı.`);
  };

  return (
    <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-800/60 pb-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-200 flex items-center gap-2">
          <Icon className={`w-4 h-4 ${iconColor}`} /> {label}
        </Label>
        <Sparkles className={`w-4 h-4 ${iconColor}`} />
      </div>

      {/* Input + Tanımla Butonu */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-400">Yeni Alıcı E-posta Ekle</Label>
        <div className="flex gap-2">
          <Input
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder="Örn: kisi@10-11.com.tr"
            className="bg-slate-950 border-slate-800 text-xs h-11 text-slate-100 flex-1 focus:ring-1 focus:ring-emerald-500"
          />
          <Button
            type="button"
            onClick={handleAdd}
            className={`h-11 px-4 text-xs font-semibold ${btnBg} text-white shrink-0 gap-1.5 shadow-md`}
          >
            <Plus className="w-4 h-4" /> Tanımla
          </Button>
        </div>
      </div>

      {/* Tanımlı E-postalar Listesi */}
      <div className="space-y-2 pt-2 border-t border-slate-800/60">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
            Tanımlı E-posta Adresleri ({emailList.length})
          </span>
        </div>

        {emailList.length === 0 ? (
          <p className="text-xs text-slate-500 italic py-2">Henüz tanımlı alıcı adresi yok.</p>
        ) : (
          <div className="flex flex-wrap gap-2 pt-1">
            {emailList.map((email) => (
              <div
                key={email}
                className="flex items-center gap-2 bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-xl px-3 py-1.5 text-xs text-slate-200 transition-all shadow-sm"
              >
                <span className="font-mono text-[12px] text-emerald-400 font-medium">{email}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(email)}
                  className="text-slate-400 hover:text-rose-400 p-0.5 rounded-md hover:bg-rose-500/10 transition-colors flex items-center gap-1 text-[10px] font-bold"
                  title="Kaldır"
                >
                  <X className="w-3.5 h-3.5" /> Kaldır
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 pt-1">{description}</p>
      </div>
    </Card>
  );
}

export default function EmailSettings() {
  // Manuel Alıcı E-posta Listeleri (Her Durum İçin Özel Manuel Giriş)
  const [emailConfig, setEmailConfig] = useState(() => {
    const saved = localStorage.getItem("1011_email_automation_config");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {
      financeEmails: "admin@10-11.com.tr",
      videoWarningEmails: "admin@10-11.com.tr",
      todoEmails: "admin@10-11.com.tr",
      issueVideoEmails: "admin@10-11.com.tr",
    };
  });

  // Canlı Test Mail Gönderim State
  const [testMailForm, setTestMailForm] = useState({
    to: "admin@10-11.com.tr",
    subject: "10-11 E-posta Otomasyonu Canlı Test Maili",
    message: "Merhaba, bu e-posta 10-11 Yönetim Platformu Resend API bağlantı testidir.",
  });
  const [sendingTest, setSendingTest] = useState(false);

  // Ayarları Kaydet
  const saveConfig = () => {
    localStorage.setItem("1011_email_automation_config", JSON.stringify(emailConfig));
    toast.success("E-posta alıcı ayarları başarıyla kaydedildi!");
  };

  // Canlı Test Maili Gönder
  const handleSendTestMail = async () => {
    if (!testMailForm.to.trim()) {
      return toast.error("Lütfen en az bir test alıcı e-posta adresi giriniz.");
    }

    setSendingTest(true);
    toast.loading("Test e-postası Resend API üzerinden iletiliyor...", { id: "testmail" });

    try {
      const res = await sendResendEmail({
        to: testMailForm.to.split(",").map((s) => s.trim()).filter(Boolean),
        subject: testMailForm.subject,
        html: `
          <div style="font-family: Arial, sans-serif; padding: 24px; background-color: #0f172a; color: #f8fafc; border-radius: 12px; border: 1px solid #334155;">
            <h2 style="color: #38bdf8; margin-top: 0;">📧 10-11 Canlı E-posta Testi</h2>
            <p style="font-size: 15px; color: #cbd5e1;">${testMailForm.message}</p>
            <hr style="border: 0; border-top: 1px solid #334155; margin: 20px 0;" />
            <p style="font-size: 12px; color: #64748b; margin: 0;">Resend API Key (re_8q3npiXX...) İle Başarıyla İletilmiştir.</p>
          </div>
        `,
      });

      if (res && (res.id || res.success)) {
        toast.success(`🟢 Test e-postası başarıyla iletildi! (${res.provider || "E-Posta Servisi"}: ${res.id || "OK"})`, { id: "testmail" });
      } else {
        toast.error(`🔴 E-posta iletilemedi: ${res?.message || "İletim servisi yanıt vermedi."}`, { id: "testmail" });
      }
    } catch (e) {
      toast.error("Test e-postası gönderilemedi. Lütfen adresi kontrol ediniz.", { id: "testmail" });
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Üst Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" /> Resend API Canlı Entegre
            </span>
          </div>
          <h1 className="font-anton text-4xl sm:text-5xl text-white tracking-wide flex items-center gap-3">
            <Mail className="w-10 h-10 text-emerald-400" /> E-POSTA & BİLDİRİM YÖNETİMİ
          </h1>
        </div>

        <Button
          onClick={saveConfig}
          className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold shadow-lg shadow-emerald-600/20 gap-2 h-11"
        >
          <Save className="w-4 h-4" /> Tüm Alıcı Ayarlarını Kaydet
        </Button>
      </div>

      {/* API Bağlantı Kartı */}
      <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-3 relative overflow-hidden">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> Resend E-posta Servis Durumu
          </span>
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
            🟢 Aktif & Bağlı
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs space-y-1">
            <p className="text-slate-400 font-semibold">Resend API Key:</p>
            <p className="font-mono text-emerald-400 font-bold">re_8q3npiXX_J2qXMDiMZ3ZdX9k4BnAoXW67</p>
          </div>

          <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs space-y-1">
            <p className="text-slate-400 font-semibold">Gönderen Adresi (From):</p>
            <p className="font-mono text-cyan-400 font-bold">10-11 Otomasyon &lt;bildirim@10-11.xyz&gt;</p>
          </div>
        </div>
      </Card>

      {/* Manuel Alıcı Yönetim Kartları (Duruma Göre Alıcı Ekleme/Çıkarma) */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-anton text-2xl text-white tracking-wide flex items-center gap-2">
            DURUMA GÖRE MANUEL ALICI E-POSTA ADRESLERİ
          </h2>
          <span className="text-xs text-slate-400">* E-posta adresini yazıp 'Tanımla' butonuna basınız</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Durum 1: Finansal İşlemler */}
          <EmailListManager
            label="1. GELİR / GİDER İŞLEM BİLDİRİM ALICILARI"
            icon={Wallet}
            iconColor="text-emerald-400"
            btnBg="bg-emerald-600 hover:bg-emerald-500"
            emailsString={emailConfig.financeEmails}
            onChange={(val) => setEmailConfig({ ...emailConfig, financeEmails: val })}
            description="Sisteme yeni gelir veya gider eklendiğinde bu adreslere işlem detayı iletilir."
          />

          {/* Durum 2: Saatlik Video Takip & Tutarsızlık Uyarıları */}
          <EmailListManager
            label="2. SAATLİK VİDEO TUTARSIZLIK UYARI ALICILARI (🔴)"
            icon={Clock}
            iconColor="text-rose-400"
            btnBg="bg-rose-600 hover:bg-rose-500"
            emailsString={emailConfig.videoWarningEmails}
            onChange={(val) => setEmailConfig({ ...emailConfig, videoWarningEmails: val })}
            description="Saatlik video sayısı belirlenen hedefin altında kaldığında bu adreslere uyarı maili gider."
          />

          {/* Durum 3: Görev Atamaları */}
          <EmailListManager
            label="3. GÖREV / YAPILACAK İŞ BİLDİRİM ALICILARI"
            icon={ListChecks}
            iconColor="text-purple-400"
            btnBg="bg-purple-600 hover:bg-purple-500"
            emailsString={emailConfig.todoEmails}
            onChange={(val) => setEmailConfig({ ...emailConfig, todoEmails: val })}
            description="Yeni bir görev tanımlandığında veya atandığında bu adreslere bilgilendirme maili gider."
          />

          {/* Durum 4: Sorunlu / Bozuk Video Bildirimleri */}
          <EmailListManager
            label="4. BOZUK / SORUNLU VİDEO BİLDİRİM ALICILARI"
            icon={AlertTriangle}
            iconColor="text-amber-400"
            btnBg="bg-amber-600 hover:bg-amber-500"
            emailsString={emailConfig.issueVideoEmails}
            onChange={(val) => setEmailConfig({ ...emailConfig, issueVideoEmails: val })}
            description="Bir videoda görüntü/ses hatası işaretlendiğinde bu adreslere acil hata maili gider."
          />
        </div>
      </div>

      {/* Canlı Test Gönderim Kutusu */}
      <Card className="glass-card p-6 rounded-3xl border border-slate-800 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <h2 className="font-anton text-2xl text-white tracking-wide flex items-center gap-2">
            <Send className="w-5 h-5 text-cyan-400" /> CANLI TEST E-POSTASI GÖNDERME ARACI
          </h2>
          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
            Resend API Live Test
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Test Alıcı E-posta Adresi (Virgülle Birden Fazla Yazabilirsiniz)</Label>
            <Input
              value={testMailForm.to}
              onChange={(e) => setTestMailForm({ ...testMailForm, to: e.target.value })}
              placeholder="Örn: alihan@10-11.com.tr, admin@10-11.com.tr"
              className="bg-slate-950 border-slate-800 text-xs h-11"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-slate-400">E-posta Konusu (Subject)</Label>
            <Input
              value={testMailForm.subject}
              onChange={(e) => setTestMailForm({ ...testMailForm, subject: e.target.value })}
              className="bg-slate-950 border-slate-800 text-xs h-11"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-slate-400">Test Mesajı İçeriği</Label>
          <Input
            value={testMailForm.message}
            onChange={(e) => setTestMailForm({ ...testMailForm, message: e.target.value })}
            className="bg-slate-950 border-slate-800 text-xs h-11"
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSendTestMail}
            disabled={sendingTest}
            className="bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-lg shadow-cyan-600/20 gap-2 h-11 px-6"
          >
            <Send className={`w-4 h-4 ${sendingTest ? "animate-spin" : ""}`} />
            Şimdi Test Maili Gönder
          </Button>
        </div>
      </Card>
    </div>
  );
}
